import { Button } from '@components/base';
import React from 'react';
import { SideBarStates, useSidebarDispatch } from '@components/context/SidebarContext';
import { BasePanelComponent } from './CommonComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import NavLink from 'next/link';
import { useUserSession } from '@hooks/user/useDefaultSession';
import {
    AccountUsersQuery,
    FileEntry,
    useAccountUsersQuery,
    User,
    useRealUserQuery,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { Permissions, Roles, useUserPermissions } from '@hooks/user/useUserPermissions';
import { useQueryClient } from 'react-query';
import { useImpersonateUser } from '@hooks/user/useImpersonateUser';
import router from 'next/router';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { SkipperAvatar } from '@skipper/ui';
import { useFileEntry } from '@hooks/files/useFileEntry';

const CurrentYear = new Date().getFullYear();

export function AccountPanel() {
    const [user] = useUserSession();
    const queryClient = useQueryClient();
    const { data } = useRealUserQuery(getClient('users'), undefined, {
        staleTime: 1000 * 60 * 5, // 5 mins
    });

    const imageUrl = useFileEntry({ file: user?.imageFileEntry as FileEntry });
    const { data: AccountUserData, isLoading } = useAccountUsersQuery(
        getClient('users'),
        undefined,
        {
            staleTime: 60 * 1000 * 5,
        },
    );
    const systemAdmin = data?.realMe?.systemAdmin;

    const adultUser = AccountUserData?.accountUsers?.find(
        (u) => u.accountRole?.roleType !== 'child',
    );
    const accountUsers = AccountUserData?.accountUsers?.filter(
        (u) => ![data?.realMe?.id, adultUser?.id].includes(u.id),
    );

    return (
        <BasePanelComponent openState={SideBarStates.Profile}>
            <BasePanelComponent.ContentlessHeader>
                <div className="flex flex-1 w-full items-center border-b border-gray-300 px-4 py-3 gap-x-4">
                    <SkipperAvatar {...user} imageUrl={imageUrl} size="large" />
                    <h2 className="text-xl text-red-600">Hi, {user?.firstName}</h2>
                </div>
            </BasePanelComponent.ContentlessHeader>
            <BasePanelComponent.Body className="px-4 sm:px-6 pt-6">
                <TopLevelAccount {...(data?.realMe as any)} />
                {(data?.realMe?.accountRole?.roleType === Roles.Adult || systemAdmin) && (
                    <>
                        <AccountOwner
                            {...(adultUser ? (adultUser as any) : (data?.realMe as any))}
                            isViewing={data?.realMe?.id === user?.id || adultUser?.id === user?.id}
                        />
                        <ViewAs
                            accountUsers={accountUsers as User[]}
                            ignoredId={data?.realMe?.id ?? ''}
                            activeId={user?.id ?? ''}
                            isLoading={isLoading}
                        />
                    </>
                )}
                <ShowNavlinks isSystemAdmin={systemAdmin} />
                <div className="w-full flex flex-col flex-1 mt-8">
                    <Button
                        className="w-full flex items-center justify-center font-bold"
                        onClick={(e) => {
                            e.preventDefault();
                            fetch('/api/signout', {
                                method: 'POST',
                                credentials: 'include',
                            })
                                .then(() => {})
                                .catch(() => {})
                                .finally(() => {
                                    router.replace('/auth/signin');
                                    queryClient.removeQueries();
                                });
                        }}>
                        LOG OUT
                    </Button>
                    <div className="text-center mt-2 pb-2">
                        <p className="text-sm text-gray-400">
                            Copyright &#169; {CurrentYear} Skipper, LLC.
                            <NavLink href="/privacy.html">
                                <span className="text-teal-600">&nbsp; Privacy Policy</span>
                            </NavLink>
                        </p>
                    </div>
                </div>
            </BasePanelComponent.Body>
        </BasePanelComponent>
    );
}

function TopLevelAccount(user: User) {
    const impersonate = useImpersonateUser();
    return (
        <div className="w-full mb-3">
            <TextUnderlineHeader text="LOGGED IN AS:" />
            <div className="flex items-center py-2 px-2 h-full">
                <AvatarUserRow
                    {...user}
                    showRole
                    onClick={() => impersonate.mutate({ id: user.id })}
                />
            </div>
        </div>
    );
}

function AccountOwner({
    id,
    fullName,
    initials,
    isViewing,
    calendarColor,
    imageFileEntry,
    ...rest
}: User & { isViewing?: boolean }) {
    const mutate = useImpersonateUser();
    return (
        <div className="w-full mb-3">
            <TextUnderlineHeader text="ACCOUNT OWNER:" />
            <div className="relative flex items-center py-2 px-2">
                <AvatarUserRow
                    fullName={fullName ?? ''}
                    imageFileEntry={imageFileEntry as FileEntry}
                    calendarColor={calendarColor ?? ''}
                    initials={initials ?? ''}
                    onClick={() => {
                        mutate.mutate({ id });
                    }}
                    {...rest}
                />
                <div className="absolute right-2">
                    {isViewing && (
                        <FontAwesomeIcon
                            icon={faEye}
                            size="lg"
                            className="fill-current text-gray-300"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function ViewAs({
    ignoredId,
    activeId,
    accountUsers,
    isLoading,
}: {
    ignoredId: string;
    activeId?: string;
    accountUsers?: User[];
    isLoading: boolean;
}) {
    const mutate = useImpersonateUser();

    if (isLoading) {
        return (
            <div>
                <SimpleSpinner />
            </div>
        );
    }

    if (accountUsers?.length === 0) {
        return null;
    }

    return (
        <div className="w-full mb-3">
            <TextUnderlineHeader text="VIEW AS:" />
            <div className="grid grid-cols-1 items-center py-2 px-2 gap-y-3">
                {accountUsers?.map((user) => {
                    return (
                        <div key={user.id} className="flex">
                            <AvatarUserRow
                                {...user}
                                onClick={() => {
                                    mutate.mutate({ id: user.id });
                                }}
                                isViewing={activeId === user.id}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AvatarUserRow({
    showRole = false,
    onClick,
    initials,
    calendarColor,
    fullName,
    isViewing,
    imageFileEntry,
    accountRole,
}: Pick<
    NonNullable<AccountUsersQuery['accountUsers']>[0],
    'imageFileEntry' | 'initials' | 'fullName' | 'calendarColor' | 'accountRole'
> & {
    onClick?: () => void;
    showRole?: boolean;
    isViewing?: boolean;
}) {
    const imageUrl = useFileEntry({ file: imageFileEntry as FileEntry });

    return (
        <button className="relative flex flex-1 items-center w-full" onClick={onClick}>
            <div className="mr-4">
                <SkipperAvatar
                    initials={initials}
                    imageFileEntry={imageFileEntry as FileEntry}
                    calendarColor={calendarColor}
                    imageUrl={imageUrl}
                    size="medium"
                />
            </div>
            <div className="flex justify-center flex-col text-left">
                <div
                    className={`text-lg cursor-pointer ${
                        isViewing ? 'text-magenta-600' : 'text-teal-600'
                    }`}>
                    {fullName}
                </div>
                {showRole && accountRole && <div className="text-gray-400">{accountRole.name}</div>}
            </div>
            <div className="absolute right-2">
                {isViewing && (
                    <FontAwesomeIcon
                        icon={faEye}
                        size="lg"
                        className="fill-current text-gray-300"
                    />
                )}
            </div>
        </button>
    );
}

function TextUnderlineHeader({ text }: { text: string }) {
    return (
        <div className="pr-40 py-2 border-b border-teal-600 pl-3">
            <div className="text-sm">{text}</div>
        </div>
    );
}

const navRoutes = [
    {
        name: 'Profile',
        route: '/users/edit',
        className: 'text-teal-600 hover:text-teal-900',
        permissionCheck: Permissions.manage_profile,
    },
    {
        name: 'Manage Family',
        route: '/account/seats',
        className: 'text-teal-600 hover:text-teal-900',
        roleCheck: Roles.Adult,
    },
    {
        name: 'My Enrollments',
        route: '/account/enrollments',
        className: 'text-teal-600 hover:text-teal-900',
    },
    {
        name: 'Manage Courses',
        route: '/account/manage-active',
        className: 'text-teal-600 hover:text-teal-900',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Manage Members',
        route: '/account/manage-members',
        className: 'text-teal-600 hover:text-teal-900',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Notifications',
        route: '/users/notifications',
        className: 'text-teal-600 hover:text-teal-900',
        permissionCheck: Permissions.notification_settings,
    },
    {
        name: 'Account Settings',
        route: '/account/settings',
        className: 'text-teal-600 hover:text-teal-900',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Billing and Plans',
        route: '/account/billing',
        className: 'text-teal-600 hover:text-teal-900',
        roleCheck: Roles.Adult,
    },
    {
        name: 'Permission Levels',
        route: '/account/permission-levels',
        className: 'text-teal-600 hover:text-teal-900',
        roleCheck: Roles.Adult,
    },
    {
        name: 'System Admin Panel',
        route: '/admin/users',
        className: 'text-red-600',
        systemAdminCheck: true,
    },
];

function ShowNavlinks({ isSystemAdmin }: { isSystemAdmin: boolean }) {
    const { hasPermission, hasRole } = useUserPermissions();
    const { toggle: sidebarToggle } = useSidebarDispatch();

    return (
        <div className="flex">
            <div className="grid grid-cols-1 divide-y-2 divide-teal-600 w-full">
                {navRoutes
                    .filter((r) => {
                        if (!!r.permissionCheck) {
                            return hasPermission(r.permissionCheck);
                        }
                        if (!!r.roleCheck) {
                            return hasRole(r.roleCheck);
                        }
                        if (!!r.systemAdminCheck) {
                            return isSystemAdmin;
                        }

                        return true;
                    })
                    .map((route) => {
                        return (
                            <NavLink key={route.name} href={route.route}>
                                <div
                                    key={route.name}
                                    onClick={() => sidebarToggle(SideBarStates.Closed)}
                                    className={`${route.className} py-3 px-4 text-lg cursor-pointer `}>
                                    {route.name}
                                </div>
                            </NavLink>
                        );
                    })}
            </div>
        </div>
    );
}
